

import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  Input,
  isDevMode,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { EMPTY, merge, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { isNotNil } from 'mwc/core/util';

import { MwcInputDirective } from './input.directive';

@Component({
  selector: 'mwc-textarea-count',
  template: ` <ng-content select="[mwc-input]"></ng-content> `,
  host: {
    class: 'mwc-input-textarea-show-count'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MwcTextareaCountComponent implements AfterContentInit, OnDestroy {
  @ContentChild(MwcInputDirective, { static: true }) mwcInputDirective!: MwcInputDirective;
  @Input() mwcMaxCharacterCount: number = 0;
  @Input() mwcComputeCharacterCount: (v: string) => number = v => v.length;
  @Input() mwcFormatter: (cur: number, max: number) => string = (c, m) => `${c}${m > 0 ? `/${m}` : ``}`;

  private configChange$ = new Subject();
  private destroy$ = new Subject();

  constructor(private renderer: Renderer2, private elementRef: ElementRef<HTMLElement>) {}

  ngAfterContentInit(): void {
    if (!this.mwcInputDirective && isDevMode()) {
      throw new Error('[mwc-textarea-count]: Could not find matching [mwc-input] child.');
    }

    if (this.mwcInputDirective.ngControl) {
      const valueChanges = this.mwcInputDirective.ngControl.valueChanges || EMPTY;
      merge(valueChanges, this.configChange$)
        .pipe(
          takeUntil(this.destroy$),
          map(() => this.mwcInputDirective.ngControl.value),
          startWith(this.mwcInputDirective.ngControl.value as string)
        )
        .subscribe(value => {
          this.setDataCount(value);
        });
    }
  }

  setDataCount(value: string): void {
    const inputValue = isNotNil(value) ? String(value) : '';
    const currentCount = this.mwcComputeCharacterCount(inputValue);
    const dataCount = this.mwcFormatter(currentCount, this.mwcMaxCharacterCount);
    this.renderer.setAttribute(this.elementRef.nativeElement, 'data-count', dataCount);
  }

  ngOnDestroy(): void {
    this.configChange$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
